
import { DeepCamelToSnake, WrapResponse } from '@/types';
import { StateSlice } from '@/stores/appStore';
import { snakeToCamel } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import { ID, Paper, PaperId } from '@/stores/slices/paperSlice';
import { shallow } from 'zustand/shallow';
import { api, host } from '@/lib/env';
import { uuid } from '@/lib/uuid';
import { fun } from '@/lib/request';


export type SummaryId = ID | number;
export type SummaryStepName = 'outline' | 'outline_update' | 'summary';
export type WorkflowItemNodeContent = {
  summaryId: SummaryId;
  schedule: string;
  status: number;
  step: SummaryStepName;
  relatedPaper: string[];
  createTime: string;
  extra: {
    lang: string;
    platform: string;
    subject: string;
    template: string;
    relatedPaper: string[];
  }
}
export type WorkflowItemNode = {
  content: WorkflowItemNodeContent
}
export type WorkflowItem = {
  wfId: number;
  scene: 'summary'; // 以后还有其他更多类型
  isExample: 0 | 1 | boolean; // 是否是示例
  wfName: string;
  createTime: string;
  nodeNum: number;
  maxWordsNumber: number;
  firstNode: WorkflowItemNode;
  lastNode: WorkflowItemNode;
}


export type Contents = {
  paperId: PaperId[];
  subject: string;
  extraRequire: string;
  template: 'pro' | 'standard' | 'simple';
  lang: 'zh' | 'en';
  citationFormat: 'GB/T 7714-2015' | 'apa' | 'mla' | 'chicago' | 'ieee' | 'ama' | 'harvard';
  paperList?: Paper[];
  mode: 'deep' | 'standard' | 'pro' | 'max';
};
export type SnakeContents = DeepCamelToSnake<Contents>;

export type OnContentsChange = (
  key: keyof SnakeContents,
  value: SnakeContents[keyof SnakeContents],
  contents: SnakeContents,
) => void;

export type CreateOutlineOptions = {
  wfId: ID;
  summaryId?: SummaryId;
  contents: SnakeContents;
  paperList?: Paper[];
  outline?: string;
  couponId: ID;
};
export type CreateOutlineData = {
  wfId: ID;
  summaryId: SummaryId;
  failedPaper?: Paper[];
  processPaper?: Paper[];
  summaryInfo: {
    summaryId: SummaryId;
    status: number;
    step: SummaryStepName;
  }
};
export type StartSummaryOptions = {
  wfId: ID;
  summaryId: SummaryId;
  outline: string;
};
export type StartSummaryData = CreateOutlineOptions;

export type FetchSummaryOptions = {
  id: SummaryId;
  resetContent?: boolean;
}

export type SaveSummaryOptions = {
  id: SummaryId;
  summaryContent: string;
}

// 综述详情
export type Summary = Record<string, any> & {
  summaryId: SummaryId;
  editContent: string;
  summaryContent: string;
  /**
   * 0：待执行；
   * 1：执行中；
   * 2：执行完成；
   * <0: 失败；
   * -1：生成参数错误；
   * -30010：文献解析失败；
   * -6002：AI调用失败；
   */
  // export type ParseStatus = 0 | 1 | 2 | -1 | -30010;
  status: number;
  // 当前处理进度文本
  schedule: string;
  createTime: string;
  paperId: number[];
  paperList: Paper[];
  contentLength?: number;
  failedPaper?: Paper[];
  // 新增当前节点字段，outline：目录大纲, outline_update：更新目录大概，summary：综述内容
  step: SummaryStepName;
  outlineList: RawOutlineItem[];
  extra: Contents;
  // 优化建议
  suggestions: string[];
  isExample: 0 | 1 | boolean; // 是否是示例
  /** 大纲 Agent 引导区 Markdown，来自 detail 接口 `outline_welcome` */
  outlineWelcome?: string;
}

/** 从 `/paper/summary/detail` 响应中取 `outline_welcome`（兼容驼峰/蛇形及落在 `extra` 的情况） */
export function getOutlineWelcomeFromSummary(
  detail: Summary | null | undefined,
): string | undefined {
  if (!detail) {
    return undefined;
  }
  const raw = detail as Record<string, unknown>;
  const candidates: unknown[] = [
    detail.outlineWelcome,
    raw.outline_welcome,
  ];
  const extra = detail.extra as Record<string, unknown> | undefined;
  if (extra && typeof extra === "object") {
    candidates.push(extra.outlineWelcome, extra.outline_welcome);
  }
  for (const item of candidates) {
    if (typeof item === "string" && item.trim()) {
      return item;
    }
  }
  return undefined;
}

// 大纲
export type RawOutlineItem = {
  title: string;
  subTitle: string;
  requirement: string;
  references: string[];
  level: number;
}


export type OutlineItem = {
  pid: string;
  id: string;
  content: string;
  subTitle: string;
  subs: OutlineItem[];
  level: number;
  readonly: boolean;
  extra?: {
    [key: string]: any;
  };
}

// 消息类型
export type Role =
  'user' |
  // 文献综述创建
  'user.summaryCreate' |
  // 文献综述重写
  'user.summaryRewrite' |
  'assistant' |
  // 文献综述总结
  'assistant.summary' |
  'system' |
  // 文献综述生成中
  'system.summaryCreating' |
  // 文献综述创建
  'system.summaryNewCreated' |
  // 联系
  'system.concat' |
  // 邀请
  'system.invite' |
  // 超出
  'system.exceedNumberOfChat'
export type SummaryHistoryItem = {
  method: 'create' | 'createoutline' | 'rewrite' | 'summary';
  content: (Contents & {
    paperList: Paper[];
    createTime: string;
    summaryId: SummaryId;
    outline: string;
  }) | Summary;
  createTime: string;
  role: Role;
};
export type FetchSummaryHistoryData = SummaryHistoryItem[];

export type WorkFlowsData = {
  list: WorkflowItem[];
  consumeCount: number
}

export type WorkFlowsOtherData = {
  consumeCount: number
}

export type CheckPapersOptions = {
  paperIds: PaperId[]
};
export type PaperStatus = {
  failedPaper: Paper[];
  paperCount: number;
  processPaper: Paper[];
}
export type PaperStatusData = WrapResponse<PaperStatus, false>;

export type OptimizeOutlineOptions = {
  wfId: ID;
  contents: SnakeContents;
  outline: string;
  suggestion: string;
};

export type OptimizeOutlineData = {
  wfId: ID;
  outline: RawOutlineItem[];
};

export type OptimizeFreeTimesOptions = {
  wfId: string;
}
export type OptimizeFreeTimesData = {
  optimizeOutline: number;
  /** 与 optimize_outline 同接口返回时优先用于大纲 Agent；缺省则回退 optimizeOutline */
  outlineAgent?: number;
}

const workflowsNS = 'workflows';
export type WorkflowSlice = {
  [workflowsNS]: {
    all: WorkflowItem[]; // entities
    current: null | WorkflowItem; // current
    fetching: boolean;
    silentFetching: boolean;
    consumeCount: number | null;
    setFetching: (loading: boolean, silent: boolean) => void;
    fetch: (silent?: boolean) => Promise<boolean>;
    fetchConsumeCount: () => Promise<boolean>;
    remove: (ids: string[]) => Promise<boolean>;
    selectedIds: PaperId[];
    setSelectedIds: (ids: PaperId[]) => void;
    outlines: OutlineItem[];
    outlineFocus: string;
    outlineDeleteConfirm: string;
    outlineDeleteConfirmCallback: ((confirm: boolean) => void) | null;
    outlinesMethods: {
      // 大纲创建中
      creating: boolean;
      // 检查paper是否解析完成或者有解析错误
      checkPapers: (options: CheckPapersOptions) => Promise<PaperStatusData>;
      // 创建大纲
      create: (options: CreateOutlineOptions) => Promise<WrapResponse<CreateOutlineData, false>>;
      // 优化目录
      optimize: (options: OptimizeOutlineOptions) => Promise<OptimizeOutlineData>;
      // 载入大纲
      load(outlines: OutlineItem[]): void;
      // 更新大纲节点内容
      updateItemContent(id: string, content: string, changeType?: 'content' | 'subTitle'): void;
      addItem(pid: string, index?: number, content?: string): OutlineItem;
      // replaceItem(id: string, item: OutlineItem): boolean;
      deleteItem(id: string): OutlineItem | null;
      findItem(id: string): OutlineItem | null;
      // moveItem(activeId: string, overId: string): boolean;
      moveItemTo(fromItemId: string, toItemId: string, toIndex: number): boolean;
      moveItemUp(id: string): boolean;
      moveItemDown(id: string): boolean;
      findSiblings(id: string): OutlineItem[];
      findItemIndex(id: string): number;
      findParentItem(id: string): OutlineItem | null;
      showDeleteConfirm(id: string, callback?: ((confirm: boolean) => void) | null): void;
      setFocus(id: string): void;
    };
    refreshOptimizeFreeTimes: (options: OptimizeFreeTimesOptions) => Promise<number>,
    fetchSummaryFreeTimes: (options: OptimizeFreeTimesOptions) => Promise<OptimizeFreeTimesData>,
    fetchSummaryDetail(options: FetchSummaryOptions): Promise<Summary>;
    saveSummaryDetail(options: SaveSummaryOptions): Promise<WrapResponse<{}, false>>;
    setCitationFormat(options: { summaryId: SummaryId; citationFormat: string }): Promise<boolean>;
    history: SummaryHistoryItem[];
    fetchingSummaryHistory: boolean;
    fetchSummaryHistory(wfId: string): Promise<SummaryHistoryItem[]>;
    startSummary(options: StartSummaryOptions): Promise<WrapResponse<StartSummaryData, false>>;
  }
};

let firstFetched = false;
export const createWorkflowSlice: StateSlice<WorkflowSlice> = (set, _get) => {
  const get = () => _get()[workflowsNS];
  return {
    [workflowsNS]: {
      all: [],
      current: null,
      fetching: false,
      silentFetching: false,
      consumeCount: null,
      setFetching: (loading, silent = false) => {
        set(({ [workflowsNS]: state }) => {
          if (!silent) {
            state.fetching = loading;
          } else {
            state.silentFetching = loading;
          }
        });
      },
      fetch: async (silent = false) => {
        const { setFetching, fetching, silentFetching } = get();
        if (firstFetched) {
          // 已经在请求中了忽略
          if (fetching || silentFetching) {
            return true;
          }
        } else {
          firstFetched = true;
        }
        setFetching(true, silent);
        try {
          const { data, code, message } = (
            await fun.post<WrapResponse<WorkFlowsData>>(
              api('/workflow/main', true),
            )
          ).data;
          const { list: workflows, consumeCount } = snakeToCamel<WorkFlowsData>(data);
          if (code === 0) {
            if (!shallow(workflows, get().all)) {
              set(({ [workflowsNS]: state }) => {
                state.all = workflows;
                state.consumeCount = consumeCount;
              });
            }
            return true;
          }
          toast.error(message);
          return false;
        } catch (error) {
          console.error('Error fetch paper workflows:', error);
          return false;
        } finally {
          setFetching(false, silent);
        }
      },
      fetchConsumeCount: async () => {
        try {
          const { data, code, message } = (
            await fun.post<WrapResponse<WorkFlowsOtherData>>(
              api('/workflow/main/others', true),
            )
          ).data;
          const { consumeCount } = snakeToCamel<WorkFlowsOtherData>(data);
          if (code === 0) {
            set(({ [workflowsNS]: state }) => {
              state.consumeCount = consumeCount;
            });
            return true;
          }
          toast.error(message);
          return false;
        } catch (error) {
          console.error('Error fetch workflows:', error);
          return false;
        } finally {
        }
      },
      remove: async (ids) => {
        try {
          const { code, message } = (await fun.post<WrapResponse<{}>>(
            api('/workflow/main/delete', true), {
            wf_id: ids
          }
          )).data;
          if (code === 0) {
            set(({ [workflowsNS]: state }) => {
              state.all = state.all.filter((item) => !ids.includes(item.wfId.toString()));
            });
            return true;
          } else {
            toast.error(message);
          }
          return false;
        } catch (error) {
          console.error('Error delete summarize:', error);
          return false;
        } finally {

        }
      },
      // wfId: '',
      // summaryId: '',
      selectedIds: [],
      setSelectedIds: (ids) => {
        set(({ [workflowsNS]: state }) => {
          state.selectedIds = ids;
        });
      },
      outlines: [],
      outlineFocus: '',
      outlineDeleteConfirm: '',
      outlineDeleteConfirmCallback: null,
      outlinesMethods: {
        creating: false,
        create: async (options: CreateOutlineOptions) => {
          set(({ [workflowsNS]: state }) => {
            state.outlinesMethods.creating = true;
          });
          const data = (await fun.post<WrapResponse<CreateOutlineData>>(
            api('/workflow/summary/createOutline', true),
            {
              ...options.contents,
              wf_id: options.wfId,
              summary_id: options.summaryId,
              outline: options.outline,
              coupon_id: options.couponId,
            }
          )).data;
          await _get().refreshUserInfo();
          set(({ [workflowsNS]: state }) => {
            state.outlinesMethods.creating = false;
          });
          return snakeToCamel<WrapResponse<CreateOutlineData, false>>(data);
        },
        optimize: async (options) => {
          set(({ [workflowsNS]: state }) => {
            state.outlinesMethods.creating = true;
          });
          const { data } = (await fun.post<WrapResponse<OptimizeOutlineData>>(
            api('/workflow/summary/optimizeOutline', true),
            {
              ...options.contents,
              wf_id: options.wfId,
              outline: options.outline,
              suggestion: options.suggestion,
            }
          )).data;
          await _get().refreshUserInfo();
          set(({ [workflowsNS]: state }) => {
            state.outlinesMethods.creating = false;
          });
          return snakeToCamel<OptimizeOutlineData>(data);
        },
        checkPapers: async (options: CheckPapersOptions) => {
          const data = (await fun.post<WrapResponse<PaperStatus>>(
            api('/workflow/summary/checkPaper', true),
            {
              paper_id: options.paperIds
            }
          )).data;
          return snakeToCamel<WrapResponse<PaperStatus, false>>(data);
        },
        load(outlines: OutlineItem[]) {
          const formatItem = (items: OutlineItem[], pid: string = '', level: number = 1): OutlineItem[] => {
            return items.map(item => {
              // 设置当前节点的 pid 和 level
              return {
                ...item,
                pid,
                level,
                subs: item.subs ? formatItem(item.subs, item.id, level + 1) : []
              } as OutlineItem;
            });
          };
          set(({ [workflowsNS]: state }) => {
            state.outlines = formatItem(outlines);
          });
        },
        updateItemContent: (id, value, changeType = 'content') => {
          // 递归函数，用于深度搜索并更新节点内容
          const updateContent = (items: OutlineItem[]): OutlineItem[] => {
            return items.map(item => {
              // 如果找到对应ID的节点，更新内容
              if (item.id === id) {
                return { ...item, [changeType]: value };
              }
              // 递归处理子节点
              if (item.subs) {
                const updatedSubs = updateContent(item.subs);
                return { ...item, subs: updatedSubs };
              }
              return item;
            });
          };
          // 从根节点开始更新操作
          set(({ [workflowsNS]: state }) => {
            state.outlines = updateContent(copy(get().outlines));
          });
        },
        // 添加新节点
        addItem(pid: string, index: number = -1, content: string = '') {
          // 创建新节点
          const newItem: OutlineItem = {
            pid,
            id: uuid(),
            content,
            subTitle: '',
            subs: [],
            level: 1,
            readonly: false,
          };

          // 递归函数，用于在指定位置插入节点
          const addItem = (items: OutlineItem[], pid: string, item: OutlineItem, index: number) => {
            // 根节点
            if (!pid) {
              let idx = index;
              if (idx === -1) {
                idx = items?.length ?? 0;
              }
              items.splice(idx, 0, item);
              return items;
            }
            // 循环每个node
            items.forEach((node) => {
              let idx = index;
              if (idx === -1) {
                idx = node?.subs?.length ?? 0;
              }
              if (node.id === pid) {
                if (!node.subs) {
                  node.subs = [];
                }
                item.level = node.level + 1;
                // 插入节点到指定索引位置
                node.subs.splice(idx, 0, item);
              } else if (node.subs) {
                node.subs = addItem(node.subs, pid, item, idx);
              }
            });
            return items;
          };
          // 在根节点中插入新节点
          const outlines = addItem(copy(get().outlines), pid, newItem, index);
          set(({ [workflowsNS]: state }) => {
            state.outlines = outlines;
            state.outlineFocus = newItem.id;
          });
          return newItem;
        },
        deleteItem(id: string) {
          let node = null;
          // 递归函数，用于深度搜索并删除子节点
          const removeSubItems = (items: OutlineItem[]): OutlineItem[] => {
            return items.reduce((acc, item) => {
              // 如果当前节点ID不是要删除的ID，则保留该节点
              if (item.id !== id) {
                // 递归处理子节点
                const subs = item.subs ? removeSubItems(item.subs) : [];
                acc.push({ ...item, subs });
              } else {
                node = item;
              }
              return acc;
            }, [] as OutlineItem[]);
          };

          // 从根节点开始删除操作
          const outlines = removeSubItems(copy(get().outlines));
          set(({ [workflowsNS]: state }) => {
            state.outlines = outlines;
          });
          // 返回节点
          return node;
        },
        findItem(id: string) {
          const outlines = copy(get().outlines) as OutlineItem[];
          if (!id) {
            return null;
          }

          const foundItem = findItemRecursive(outlines, id);
          return foundItem;
        },
        moveItemTo(fromItemId: string, toItemId: string, toIndex: number) {
          const outlines: OutlineItem[] = copy(get().outlines);
          const success = moveItemTo(outlines, fromItemId, toItemId, toIndex);
          if (success) {
            set(({ [workflowsNS]: state }) => {
              state.outlines = outlines;
            });
          }
          return success;
        },
        moveItemUp(itemId: string) {
          const outlines: OutlineItem[] = copy(get().outlines);
          const item = findItemRecursive(outlines, itemId);

          if (!item) return false; // 如果没有找到节点或节点是根节点，返回false

          const siblings = (item.pid && findItemRecursive(outlines, item.pid)?.subs) || outlines;
          const index = siblings.findIndex(sibling => sibling.id === itemId);
          if (index === -1 || index === 0) return false; // 如果节点是第一个子节点，无法上移

          // 将节点移动到当前位置的前一个位置
          const success = moveItemTo(outlines, itemId, item.pid, index - 1);
          if (success) {
            set(({ [workflowsNS]: state }) => {
              state.outlines = outlines;
            });
          }
          return success;
        },
        moveItemDown(itemId: string) {
          const outlines: OutlineItem[] = copy(get().outlines);
          const item = findItemRecursive(outlines, itemId);
          if (!item) return false; // 如果没有找到节点或节点是根节点，返回false

          const siblings = (item.pid && findItemRecursive(outlines, item.pid)?.subs) || outlines;
          const index = siblings.findIndex(sibling => sibling.id === itemId);
          if (index === -1 || index === siblings.length - 1) return false; // 如果节点是最后一个子节点，无法下移

          // 将节点移动到当前位置的后一个位置
          const success = moveItemTo(outlines, itemId, item.pid, index + 1);
          if (success) {
            set(({ [workflowsNS]: state }) => {
              state.outlines = outlines;
            });
          }
          return success;
        },
        findItemIndex(id: string) {
          if (!id) {
            return -1;
          }
          const findItemIndexRecursive = (items: OutlineItem[], id: string): number => {
            for (let i = 0; i < items.length; i++) {
              if (items[i].id === id) {
                return i; // 返回全局索引
              }
              if (items[i].subs) {
                const subIndex = findItemIndexRecursive(items[i].subs, id);
                if (subIndex !== -1) {
                  return subIndex; // 如果在子节点中找到，返回索引
                }
              }
            }
            return -1; // 如果遍历完所有节点都没有找到，返回 -1
          };
          return findItemIndexRecursive(copy(get().outlines), id);
        },
        findSiblings(id: string) {
          const outlines = copy(get().outlines) as OutlineItem[];
          const p = get().outlinesMethods.findParentItem(id) as (OutlineItem | null);
          return p?.subs || outlines;
        },
        findParentItem(id: string) {
          const outlines = copy(get().outlines) as OutlineItem[];
          if (!id) {
            return null;
          }
          const recursiveSearch = (items: OutlineItem[], id: string): OutlineItem | null => {
            // 遍历大纲项
            for (const item of items) {
              if (item.subs && item.subs.some(subItem => subItem.id === id)) {
                // 如果找到，返回当前节点作为父节点
                return item;
              }
              // 如果当前节点有子节点，继续递归搜索
              if (item.subs) {
                const parentItem = recursiveSearch(item.subs, id);
                if (parentItem) {
                  return parentItem; // 如果在子节点中找到父节点，返回该父节点
                }
              }
            }
            return null;
          };
          const parentItem = recursiveSearch(outlines, id);
          return parentItem;
        },
        showDeleteConfirm(id, callback = null) {
          set(({ [workflowsNS]: state }) => {
            state.outlineDeleteConfirm = id;
            state.outlineDeleteConfirmCallback = callback;
          });
        },
        setFocus(id: string) {
          set(({ [workflowsNS]: state }) => {
            state.outlineFocus = id;
          });
        }
      },
      fetchSummaryFreeTimes: async (options: OptimizeFreeTimesOptions) => {
        const { data } = (await fun.post<WrapResponse<Record<string, unknown>>>(
          api('/workflow/summary/freeTimes', true), {
          wf_id: options.wfId,
        }
        )).data;
        const camel = snakeToCamel(data ?? {}) as Partial<OptimizeFreeTimesData>;
        return {
          optimizeOutline: typeof camel.optimizeOutline === "number" ? camel.optimizeOutline : 0,
          outlineAgent: typeof camel.outlineAgent === "number" ? camel.outlineAgent : undefined,
        };
      },
      refreshOptimizeFreeTimes: async (options) => {
        const d = await _get()[workflowsNS].fetchSummaryFreeTimes(options);
        return d.optimizeOutline;
      },
      startSummary: async (options: StartSummaryOptions) => {
        const data = (await fun.post<WrapResponse<StartSummaryData>>(
          api('/workflow/summary/start', true), {
          wf_id: options.wfId,
          summary_id: options.summaryId,
          outline: options.outline,
        }
        )).data;
        return snakeToCamel<WrapResponse<StartSummaryData, false>>(data);
      },
      fetchSummaryDetail: async (options: FetchSummaryOptions) => {
        const { code, data, message } = (await fun.post<WrapResponse<Summary>>(
          api('/paper/summary/detail', true),
          snakeToCamel<FetchSummaryOptions, false>(options, {
            reverse: true,
          })
        )).data;
        if (code !== 0) {
          toast.error(message);
        }
        const ret = snakeToCamel<Summary>(data);
        return ret;
      },
      saveSummaryDetail: async (options: SaveSummaryOptions) => {
        const data = (await fun.post<WrapResponse<{}>>(
          api('/paper/summary/save', true),
          snakeToCamel<SaveSummaryOptions, false>(options, {
            reverse: true,
          }),
        )).data;
        if (data.code !== 0) {
          toast.error(data.message);
        }
        return data;
      },
      setCitationFormat: async (options) => {
        try {
          const { code, message } = (await fun.post<WrapResponse<{}>>(
            api('/paper/summary/setCitationFormat', true),
            {
              summary_id: options.summaryId,
              // 小写格式
              citation_format: options.citationFormat.toLowerCase(),
            }
          )).data;
          if (code !== 0) {
            toast.error(message);
            return false;
          }
          return true;
        } catch (error) {
          console.error('Failed to set citation format:', error);
          return false;
        }
      },
      history: [],
      fetchingSummaryHistory: false,
      fetchSummaryHistory: async (wfId) => {
        let history: SummaryHistoryItem[] = [];
        set(({ [workflowsNS]: state }) => {
          state.fetchingSummaryHistory = true;
        });
        try {
          const { code, data } = (await fun.post<WrapResponse<FetchSummaryHistoryData>>(
            api('/workflow/summary/history', true),
            {
              wf_id: wfId,
            }
          )).data;
          // 检查是否为 4002 错误码（workflow 不存在）
          if (code === 4002) {
            toast.error('综述不存在', {
              id: 'summary_not_exist'
            });
            // 跳转到综述列表页面
            if (typeof window !== 'undefined') {
              window.location.href = '/summarizes';
            }
            return [];
          }
          const list = snakeToCamel<SummaryHistoryItem[]>(data);
          history = list.map((item) => {
            // 对于 summary，补充完整参数
            if (item.method === 'summary') {
              return {
                ...item,
                createTime: item.content.createTime,
                role: 'assistant' as Role,
              }
            }
            return item;
          });
          set(({ [workflowsNS]: state }) => {
            state.history = history;
          });
        } catch (error) {
          toast.error(error as string)
        } finally {
          set(({ [workflowsNS]: state }) => {
            state.fetchingSummaryHistory = false;
          });
          return history;
        }
      },
    },
  };
};

// 辅助函数：检查是否是后代节点
export function isDescendant(parent: OutlineItem, childId: string): boolean {
  if (parent.id === childId) return true;

  for (const child of parent.subs) {
    if (isDescendant(child, childId)) {
      return true;
    }
  }

  return false;
}

export const findItemRecursive = (items: OutlineItem[], id: string): OutlineItem | null => {
  // 遍历大纲项
  for (const item of items) {
    // 如果找到匹配的 ID，返回该节点
    if (item.id === id) {
      return item;
    }
    // 如果当前节点有子节点，递归搜索子节点
    if (item.subs) {
      const foundItem = findItemRecursive(item.subs, id);
      if (foundItem) {
        return foundItem;
      }
    }
  }
  return null;
};

// 移动节点的方法
export function moveItemTo(items: OutlineItem[], fromItemId: string, toItemId: string, toIndex: number): boolean {
  // 检查是否尝试将节点移动到自身
  if (fromItemId === toItemId) {
    return false;
  }

  // 查找要移动的节点
  const fromItem = findItemRecursive(items, fromItemId);
  if (!fromItem) return false; // 如果fromItemId没有找到，返回false

  // 检查是否尝试将节点移动到自己的子节点中
  if (isDescendant(fromItem, toItemId)) {
    return false;
  }

  // 查找目标节点
  const toItem = toItemId ? findItemRecursive(items, toItemId) : null;
  const toItemSubs = toItem?.subs || items;

  // 从其父节点中移除fromItem
  const fromParent = fromItem.pid ? findItemRecursive(items, fromItem.pid)?.subs || items : items;
  const fromIndex = fromParent.findIndex(item => item.id === fromItemId);

  // 如果节点不在预期的父节点中，返回false
  if (fromIndex === -1) return false;

  fromParent.splice(fromIndex, 1); // 移除节点

  // 将fromItem插入到目标位置
  if (toIndex >= toItemSubs.length) {
    toItemSubs.push(fromItem); // 如果索引超出范围，添加到末尾
  } else {
    toItemSubs.splice(toIndex, 0, fromItem); // 否则，插入到指定索引位置
  }

  // 更新移动节点的pid
  fromItem.pid = toItemId || '';

  return true; // 移动成功，返回true
}

export const copy = (o: any) => {
  return (
    JSON.parse(JSON.stringify(o))
  )
}
